import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Menu, Phone } from "lucide-react";

const navItems = [
  { id: "services", label: "Services" },
  { id: "boiler", label: "Boiler Quote" },
  { id: "portfolio", label: "Portfolio" },
  { id: "about", label: "About" },
  { id: "testimonials", label: "Reviews" },
  { id: "contact", label: "Contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b border-[#E8E5DD]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="logo-home-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.png"
            alt="C4 Plumbing & Heating Ltd"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((it) => (
            <button
              key={it.id}
              data-testid={`nav-${it.id}-link`}
              onClick={() => scrollTo(it.id)}
              className="c4-link font-heading text-sm text-[#1C2A39] hover:text-[#C9A24A] transition-colors"
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-call-link"
            href="tel:+447402288642"
            className="hidden md:inline-flex items-center gap-2 text-sm text-[#1C2A39] hover:text-[#C9A24A] font-heading"
          >
            <Phone size={16} strokeWidth={1.5} />
            07402 288 642
          </a>
          <Button
            data-testid="header-quote-cta"
            onClick={() => scrollTo("quote")}
            className="hidden md:inline-flex bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-11 px-6 font-heading font-semibold tracking-wide"
          >
            Get a Quote
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="mobile-menu-trigger"
                variant="ghost"
                size="icon"
                className="lg:hidden text-[#1C2A39]"
              >
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#F9F8F6] border-l border-[#E8E5DD] w-[80%]"
              data-testid="mobile-menu-sheet"
            >
              <VisuallyHidden>
                <SheetTitle>Mobile navigation</SheetTitle>
                <SheetDescription>Navigate the C4 Plumbing & Heating site</SheetDescription>
              </VisuallyHidden>
              <div className="mt-10 flex flex-col gap-6">
                {navItems.map((it) => (
                  <button
                    key={it.id}
                    data-testid={`mobile-nav-${it.id}`}
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => scrollTo(it.id), 250);
                    }}
                    className="text-left font-heading text-2xl text-[#1C2A39]"
                  >
                    {it.label}
                  </button>
                ))}
                <Button
                  data-testid="mobile-quote-cta"
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollTo("quote"), 250);
                  }}
                  className="mt-6 bg-[#C9A24A] hover:bg-[#B0893A] text-white rounded-sm h-12"
                >
                  Get a Quote
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
